/* External */
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useRouteMatch } from 'react-router';

import { gtmEvent } from '@dtvgo/gtm-event-report';

import CommunicationAdLink from '../../components/communicationAdLink/CommunicationAdLink';
import { useGtmContext } from '../../components/GtmContext';
import { formatBannerTitle, gtmCommunicationDimensions } from '../../components/GtmContext/dimensions/communication';
import { getCommunicationCarousel } from '../../services/communicationCarousel';
import { useImpressionOnVisible } from '../../hooks/useImpressionOnVisible';
import { getLanderNameByCaption } from '../../utils';
import { mapHomeBannerPayload } from './mapHomeBannerPayload';

import './HomeBannerSlot.scss';

const PLACEHOLDER_ASSET = { __homeBannerPlaceholder: true };

/**
 * Sempre chama onFetched com um item não vazio para não travar o infinite scroll do Lander.
 */
function HomeBannerSlot({
  carouselId,
  onFetched,
  fetchEnabled,
  heroToastOverlay,
}) {
  const { gtmUserData } = useGtmContext();
  const { params: { landerUri, pageId } } = useRouteMatch();
  const landerTab = getLanderNameByCaption(landerUri || pageId);
  const communicationPayloadRef = useRef(null);
  const onFetchedRef = useRef(onFetched);
  const [banner, setBanner] = useState(null);
  const [logoFailed, setLogoFailed] = useState(false);

  onFetchedRef.current = onFetched;

  useEffect(() => {
    let cancelled = false;

    if (!fetchEnabled) {
      queueMicrotask(() => {
        if (!cancelled) onFetchedRef.current([PLACEHOLDER_ASSET]);
      });
      return () => {
        cancelled = true;
      };
    }

    setBanner(null);

    (async () => {
      const raw = await getCommunicationCarousel({ carouselId });
      if (cancelled) return;
      communicationPayloadRef.current = raw;
      setBanner(mapHomeBannerPayload(raw));
      onFetchedRef.current([PLACEHOLDER_ASSET]);
    })();

    return () => {
      cancelled = true;
    };
  }, [carouselId, fetchEnabled]);

  const bannerRef = useImpressionOnVisible({
    enabled: Boolean(banner),
    resetKey: carouselId,
    onImpression: () => {
      const { hash, eventType, dimensions } = gtmCommunicationDimensions.home_banner.page_view;
      gtmEvent({
        hash,
        eventType,
        dimensions,
        userData: { ...gtmUserData, v_banner_title: formatBannerTitle(banner.title) },
        data: { tab: landerTab },
      });
    },
  });

  const handleBannerClick = useCallback(() => {
    const { hash, eventType, dimensions } = gtmCommunicationDimensions.home_banner.banner_click;
    gtmEvent({
      hash,
      eventType,
      dimensions,
      userData: { ...gtmUserData, v_banner_title: formatBannerTitle(banner?.title) },
      data: { tab: landerTab },
    });
  }, [gtmUserData, banner?.title, landerTab]);

  if (!banner) {
    return null;
  }

  const hasImage = Boolean(banner.backgroundUrl);
  const hasLogo = Boolean(banner.logoUrl) && !logoFailed;
  const hasCtaLink = Boolean(banner.ctaLink);
  const hasCtaLabel = Boolean(banner.ctaText);
  const bannerAriaLabel = banner.title || 'Home banner';

  const containerStyle = {};
  if (hasImage) {
    containerStyle.backgroundImage = `url(${banner.backgroundUrl})`;
  } else if (banner.backgroundColor) {
    containerStyle.backgroundColor = banner.backgroundColor;
  }

  return (
    <section
      ref={bannerRef}
      className={classNames('dtv-home-banner-slot', {
        'dtv-home-banner-slot--with-image': hasImage,
        'dtv-home-banner-slot--solid': !hasImage,
        'dtv-home-banner-slot--is-link': hasCtaLink,
      })}
      style={containerStyle}
      aria-label={bannerAriaLabel}
    >
      {hasImage && <div className="dtv-home-banner-slot__overlay" aria-hidden="true" />}
      {heroToastOverlay && (
        <div className="dtv-home-banner-slot__toast-layer">
          {heroToastOverlay}
        </div>
      )}
      <div
        className={classNames('dtv-home-banner-slot__content', {
          'dtv-home-banner-slot__content--with-image': hasImage,
          'dtv-home-banner-slot__content--solid': !hasImage,
        })}
      >
        {hasLogo && (
          <img
            src={banner.logoUrl}
            alt="logo"
            className="dtv-home-banner-slot__logo"
            onError={() => setLogoFailed(true)}
          />
        )}
        {banner.title && <h3 className="dtv-home-banner-slot__title">{banner.title}</h3>}
        {hasCtaLabel && (
          <span className="dtv-home-banner-slot__cta-link">
            <span className="dtv-home-banner-slot__cta-button dtv-button dtv-btn-primary">
              <span className="dtv-button-text">{banner.ctaText}</span>
            </span>
          </span>
        )}
      </div>
      {hasCtaLink && (
        <CommunicationAdLink
          href={banner.ctaLink}
          className="dtv-home-banner-slot__hit-area"
          ariaLabel={bannerAriaLabel}
          onBeforeNavigate={handleBannerClick}
        >
          <span className="dtv-home-banner-slot__hit-area-text">{bannerAriaLabel}</span>
        </CommunicationAdLink>
      )}
    </section>
  );
}

HomeBannerSlot.propTypes = {
  carouselId: PropTypes.string.isRequired,
  onFetched: PropTypes.func.isRequired,
  fetchEnabled: PropTypes.bool,
  heroToastOverlay: PropTypes.node,
};

HomeBannerSlot.defaultProps = {
  fetchEnabled: true,
  heroToastOverlay: undefined,
};

export default HomeBannerSlot;
